import { AutoCompleteOption } from '@modules/common/components/AutoCompleteOption';
import { AutoCompleteTextField } from '@modules/common/components/AutoCompleteTextField';
import {
  getFilterValueFromType,
  upsertQueryFilterNode,
} from '@modules/queryBuilder/helpers';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import Autocomplete, {
  AutocompleteChangeReason,
  createFilterOptions,
} from '@mui/material/Autocomplete';
import { NormalisedFilter } from 'v2/querybuilder/types/querystore';

interface QueryFilterSingleSelectProps {
  id?: string;
  operator: string;
  filterField: NormalisedFilter;
  value: Option | null;
  onClose: () => void;
  open: boolean;
}

interface Option {
  id: string;
  value: string;
  displayName: string;
}

const filter = createFilterOptions<Option>();

export const QueryFilterSingleSelect = ({
  id,
  operator,
  filterField,
  value,
  onClose,
  open,
}: QueryFilterSingleSelectProps) => {
  const nodes = useQueryBuilderStore(({ query }) => query.nodes);
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);

  const onValueChange = (value: Option, reason: AutocompleteChangeReason) => {
    if (!value) {
      return;
    }

    if (reason === 'clear') {
      return;
    }

    if (isNaN(Number(value.value))) {
      return;
    }

    const updatedNodes = upsertQueryFilterNode({
      node: {
        ...filterField,
        value: getFilterValueFromType({
          value: [value.value],
          type: filterField.value.type,
        }),
      },
      nodesByID: nodes,
      parentID: filterField.parent,
    });

    dispatch({
      type: QueryBuilderActionType.SET_QUERY,
      payload: {
        nodes: updatedNodes,
      },
    });
  };

  return (
    <Autocomplete
      id={id}
      value={value}
      defaultValue={value}
      onClose={onClose}
      openOnFocus
      autoHighlight
      autoFocus
      open={open}
      onChange={(_, newValue, reason) => {
        if (newValue && typeof newValue !== 'string' && newValue.value) {
          // Create a new value from the user input
          onValueChange(
            {
              value: newValue.value,
              id: newValue.value.toString(),
              displayName: `Value ${operator}: ${newValue.value}`,
            },
            reason,
          );
        }
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        const { inputValue } = params;

        const optionValue = inputValue || value?.value || '';

        if (optionValue === '') {
          return filtered;
        }

        // Suggest the creation of a new value
        filtered.push({
          value: optionValue,
          displayName: `Value ${operator}: ${optionValue}`,
          id: optionValue.toString(),
        });

        return filtered;
      }}
      options={[]}
      getOptionLabel={(option) => {
        // Value selected with enter, right from the input
        if (typeof option === 'string') {
          return option;
        }

        if (option.value) {
          return option.value;
        }
        // Regular option
        return option.displayName;
      }}
      renderOption={(props, option) => (
        <AutoCompleteOption
          baseProps={props}
          option={option}
          searchTerm={`Value ${operator}`}
        />
      )}
      freeSolo
      disablePortal={true}
      renderInput={(params) => (
        <AutoCompleteTextField placeholder="Enter a value" {...params} />
      )}
    />
  );
};
